import React, { useEffect, useState } from 'react';
import Hero from '../components/home/Hero';
import WelcomeMessage from '../components/home/WelcomeMessage';
import OverviewSections from '../components/home/OverviewSections';
import NewsTicker from '../components/home/NewsTicker';
import VideoSection from '../components/home/VideoSection';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import OptimizedImage from '../components/ui/OptimizedImage';
import { ArrowRightIcon, CalendarIcon, GraduationCapIcon, TrophyIcon, UsersIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { AnniversaryPopup } from '../components/home/AnniversaryPopup';
const Home = () => {
  // Countdown Timer Logic
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  useEffect(() => {
    const targetDate = new Date('March 23, 2026 00:00:00').getTime();
    const updateCountdown = () => {
      const now = new Date().getTime();
      const difference = targetDate - now;
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor(difference % (1000 * 60 * 60 * 24) / (1000 * 60 * 60)),
          minutes: Math.floor(difference % (1000 * 60 * 60) / (1000 * 60)),
          seconds: Math.floor(difference % (1000 * 60) / 1000)
        });
      }
    };
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);
  return <div className="min-h-screen bg-white" data-id="element-4941">
      <AnniversaryPopup data-id="element-4942" />
      <Hero data-id="element-4943" />
      <NewsTicker data-id="element-4944" />

      {/* 70th Anniversary Banner Section */}
      <section className="relative py-12 md:py-16 bg-gradient-to-r from-blue-950 via-blue-900 to-blue-950 overflow-hidden" data-id="element-4945">
        {/* Decorative background elements */}
        <div className="absolute inset-0 opacity-10" data-id="element-4946">
          <div className="absolute top-0 left-0 w-full h-full bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMiIgY3k9IjIiIHI9IjIiIGZpbGw9IiNmZmZmZmYiLz48L3N2Zz4=')] bg-[length:30px_30px]" data-id="element-4947"></div>
        </div>
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-yellow-500 to-transparent opacity-70" data-id="element-4948"></div>
        <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-yellow-500 to-transparent opacity-70" data-id="element-4949"></div>

        <div className="container mx-auto px-4 relative z-10" data-id="element-4950">
          <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-12" data-id="element-4951">
            {/* Logo with glow effect */}
            <div className="relative group" data-id="element-4952">
              <div className="absolute inset-0 bg-yellow-400 rounded-full blur-xl opacity-20 group-hover:opacity-40 transition-opacity duration-700 animate-pulse-slow" data-id="element-4953"></div>
              <img src="https://cdn.magicpatterns.com/uploads/c7E6gNvjj8RSkSzE9h2iVJ/70th_Anniversary_logo.jpg" alt="70th Anniversary Logo" className="relative h-40 md:h-48 lg:h-56 w-auto object-contain rounded-xl shadow-2xl transform transition-transform duration-500 group-hover:scale-105" data-id="element-4954" />
            </div>

            {/* Text Content */}
            <div className="text-center md:text-left max-w-2xl" data-id="element-4955">
              <div className="inline-block mb-3" data-id="element-4956">
                <span className="text-yellow-400 font-bold tracking-widest uppercase text-sm md:text-base flex items-center justify-center md:justify-start gap-2" data-id="element-4957">
                  <span className="w-8 h-[2px] bg-yellow-400" data-id="element-4958"></span>
                  70 Years (1956 – 2026)
                  <span className="w-8 h-[2px] bg-yellow-400 md:hidden" data-id="element-4959"></span>
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-4 leading-tight drop-shadow-md" data-id="element-4960">
                Celebrating <span className="text-yellow-400" data-id="element-4961">70 Years</span> of
                Excellence
              </h2>
              <p className="text-blue-100 text-lg md:text-xl font-medium mb-6" data-id="element-4962">
                Join us as we celebrate seven decades of academic excellence,
                leadership, and legacy.
              </p>

              {/* Countdown Timer */}
              <div className="flex justify-center md:justify-start gap-4 mb-8" data-id="element-4963">
                <div className="bg-blue-900/80 border border-blue-700/50 rounded-lg p-3 min-w-[70px] text-center backdrop-blur-sm shadow-lg" data-id="element-4964">
                  <div className="text-2xl md:text-3xl font-bold text-yellow-400" data-id="element-4965">
                    {timeLeft.days}
                  </div>
                  <div className="text-xs text-blue-200 uppercase tracking-wider" data-id="element-4966">
                    Days
                  </div>
                </div>
                <div className="bg-blue-900/80 border border-blue-700/50 rounded-lg p-3 min-w-[70px] text-center backdrop-blur-sm shadow-lg" data-id="element-4967">
                  <div className="text-2xl md:text-3xl font-bold text-yellow-400" data-id="element-4968">
                    {timeLeft.hours}
                  </div>
                  <div className="text-xs text-blue-200 uppercase tracking-wider" data-id="element-4969">
                    Hours
                  </div>
                </div>
                <div className="bg-blue-900/80 border border-blue-700/50 rounded-lg p-3 min-w-[70px] text-center backdrop-blur-sm shadow-lg" data-id="element-4970">
                  <div className="text-2xl md:text-3xl font-bold text-yellow-400" data-id="element-4971">
                    {timeLeft.minutes}
                  </div>
                  <div className="text-xs text-blue-200 uppercase tracking-wider" data-id="element-4972">
                    Mins
                  </div>
                </div>
                <div className="bg-blue-900/80 border border-blue-700/50 rounded-lg p-3 min-w-[70px] text-center backdrop-blur-sm shadow-lg" data-id="element-4973">
                  <div className="text-2xl md:text-3xl font-bold text-yellow-400" data-id="element-4974">
                    {timeLeft.seconds}
                  </div>
                  <div className="text-xs text-blue-200 uppercase tracking-wider" data-id="element-4975">
                    Secs
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap justify-center md:justify-start gap-4" data-id="element-4976">
                <Button href="/anniversary#programme" variant="primary" className="bg-yellow-500 text-blue-950 hover:bg-yellow-400 border-none shadow-lg" data-id="element-4977">
                  View Programme
                </Button>
                <Button href="/anniversary#register" variant="outline" className="border-blue-300 text-blue-100 hover:bg-blue-800 hover:text-white" data-id="element-4978">
                  Register as Alumnus
                </Button>
                <Button href="/anniversary#support" variant="outline" className="border-yellow-500 text-yellow-400 hover:bg-yellow-500 hover:text-blue-950" data-id="element-4979">
                  Support Celebration
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <WelcomeMessage data-id="element-4980" />
      <OverviewSections data-id="element-4981" />
      <VideoSection data-id="element-4982" />

      {/* Recent News & Events Section */}
      <section className="py-12 md:py-16 bg-gray-50" data-id="element-4983">
        <div className="container mx-auto px-4" data-id="element-4984">
          <div className="text-center mb-10 md:mb-12" data-id="element-4985">
            <h2 className="text-2xl md:text-3xl font-bold text-blue-900 mb-3" data-id="element-4986">
              Latest News & Upcoming Events
            </h2>
            <div className="w-24 h-1 bg-yellow-500 mx-auto" data-id="element-4987"></div>
            <p className="mt-4 text-gray-600 max-w-2xl mx-auto text-sm md:text-base px-4" data-id="element-4988">
              Stay updated with the latest happenings at Isonyin Grammar School
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-id="element-4989">
            <Card hover padding="none" className="overflow-hidden" data-id="element-4990">
              <OptimizedImage src="https://uploadthingy.s3.us-west-1.amazonaws.com/xtrakyEwXvgXDeA5fP3DPg/WhatsApp_Image_2025-08-14_at_15.45.38_%284%29.jpg" alt="Modern classroom facilities" className="w-full" aspectRatio="16/9" data-id="element-4991" />
              <div className="p-5 md:p-6" data-id="element-4992">
                <div className="flex items-center text-sm text-gray-500 mb-3" data-id="element-4993">
                  <CalendarIcon className="h-4 w-4 mr-2" data-id="element-4994" />
                  January 15, 2025
                </div>
                <h3 className="text-lg md:text-xl font-bold text-blue-900 mb-3 hover:text-blue-700 transition-colors" data-id="element-4995">
                  <Link to="/news/waec-results" data-id="element-4996">
                    Outstanding WAEC Results for Class of 2024
                  </Link>
                </h3>
                <p className="text-gray-700 mb-4 text-sm md:text-base" data-id="element-4997">
                  Our graduating class has achieved exceptional results with a
                  95% distinction rate...
                </p>
                <Link to="/news/waec-results" className="inline-flex items-center text-blue-700 font-medium hover:text-blue-900 transition-colors text-sm md:text-base" data-id="element-4998">
                  Read More <ArrowRightIcon className="ml-1 h-4 w-4" data-id="element-4999" />
                </Link>
              </div>
            </Card>
            <Card hover padding="none" className="overflow-hidden" data-id="element-5000">
              <OptimizedImage src="https://cdn.magicpatterns.com/uploads/qNUMAPwjKxXJgQ7Cmi46du/WhatsApp_Image_2026-02-19_at_14.10.24_(1).jpg" alt="Students rallying for anniversary" className="w-full" aspectRatio="16/9" data-id="element-5001" />
              <div className="p-5 md:p-6" data-id="element-5002">
                <div className="flex items-center text-sm text-gray-500 mb-3" data-id="element-5003">
                  <CalendarIcon className="h-4 w-4 mr-2" data-id="element-5004" />
                  February 19, 2026
                </div>
                <h3 className="text-lg md:text-xl font-bold text-blue-900 mb-3 hover:text-blue-700 transition-colors" data-id="element-5005">
                  <Link to="/anniversary-news" data-id="element-5006">
                    Students Rally for 70th Anniversary
                  </Link>
                </h3>
                <p className="text-gray-700 mb-4 text-sm md:text-base" data-id="element-5007">
                  Current students gathered on campus in their iconic blue
                  uniforms to show excitement for the celebrations...
                </p>
                <Link to="/anniversary-news" className="inline-flex items-center text-blue-700 font-medium hover:text-blue-900 transition-colors text-sm md:text-base" data-id="element-5008">
                  Read More <ArrowRightIcon className="ml-1 h-4 w-4" data-id="element-5009" />
                </Link>
              </div>
            </Card>
            <Card hover padding="none" className="overflow-hidden" data-id="element-5010">
              <OptimizedImage src="https://cdn.magicpatterns.com/uploads/keQ1z5dAhNMgYe2HztvvFc/WhatsApp_Image_2026-02-19_at_14.06.18.jpg" alt="Anniversary planning committee meeting" className="w-full" aspectRatio="16/9" data-id="element-5011" />
              <div className="p-5 md:p-6" data-id="element-5012">
                <div className="flex items-center text-sm text-gray-500 mb-3" data-id="element-5013">
                  <CalendarIcon className="h-4 w-4 mr-2" data-id="element-5014" />
                  February 10, 2026
                </div>
                <h3 className="text-lg md:text-xl font-bold text-blue-900 mb-3 hover:text-blue-700 transition-colors" data-id="element-5015">
                  <Link to="/anniversary-news" data-id="element-5016">
                    Anniversary Planning in Full Swing
                  </Link>
                </h3>
                <p className="text-gray-700 mb-4 text-sm md:text-base" data-id="element-5017">
                  Staff and community members met to finalize preparations for
                  the grand 70th Anniversary celebration...
                </p>
                <Link to="/anniversary-news" className="inline-flex items-center text-blue-700 font-medium hover:text-blue-900 transition-colors text-sm md:text-base" data-id="element-5018">
                  Read More <ArrowRightIcon className="ml-1 h-4 w-4" data-id="element-5019" />
                </Link>
              </div>
            </Card>
          </div>
          <div className="text-center mt-8 md:mt-10" data-id="element-5020">
            <Button href="/news" variant="secondary" icon={<ArrowRightIcon data-id="element-5022" />} iconPosition="right" data-id="element-5021">
              View All News & Events
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-12 md:py-16 bg-white" data-id="element-5023">
        <div className="container mx-auto px-4" data-id="element-5024">
          <div className="text-center mb-10 md:mb-12" data-id="element-5025">
            <h2 className="text-2xl md:text-3xl font-bold text-blue-900 mb-3" data-id="element-5026">
              What Our Community Says
            </h2>
            <div className="w-24 h-1 bg-yellow-500 mx-auto" data-id="element-5027"></div>
            <p className="mt-4 text-gray-600 max-w-2xl mx-auto text-sm md:text-base px-4" data-id="element-5028">
              Hear from our students, parents, and alumni
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8" data-id="element-5029">
            <div className="bg-gray-50 p-5 md:p-6 rounded-lg border border-gray-100 shadow-sm hover:shadow-md transition-shadow" data-id="element-5030">
              <div className="flex items-center mb-4" data-id="element-5031">
                <OptimizedImage src="https://cdn.magicpatterns.com/uploads/ny9gkWcQrLZfy1eVnzqurw/WhatsApp_Image_2026-03-10_at_19.19.06_(1).jpg" alt="Principal" className="w-12 h-12 rounded-full mr-4" objectFit="cover" data-id="element-5032" />
                <div data-id="element-5033">
                  <h4 className="font-bold text-blue-900 text-sm md:text-base" data-id="element-5034">
                    Mr. Olugbenga Odunaike
                  </h4>
                  <p className="text-xs md:text-sm text-gray-500" data-id="element-5035">Principal</p>
                </div>
              </div>
              <p className="text-gray-700 italic text-sm md:text-base" data-id="element-5036">
                "At Isonyin Grammar School, we nurture character, build
                confidence, and prepare our students to become leaders."
              </p>
            </div>
            <div className="bg-gray-50 p-5 md:p-6 rounded-lg border border-gray-100 shadow-sm hover:shadow-md transition-shadow" data-id="element-5037">
              <div className="flex items-center mb-4" data-id="element-5038">
                <OptimizedImage src="https://uploadthingy.s3.us-west-1.amazonaws.com/8M2i6Lt61u8AiKMZqhL6Xz/WhatsApp_Image_2025-10-24_at_10.01.22.jpg" alt="Alumni" className="w-12 h-12 rounded-full mr-4" objectFit="cover" data-id="element-5039" />
                <div data-id="element-5040">
                  <h4 className="font-bold text-blue-900 text-sm md:text-base" data-id="element-5041">
                    Dr. Oluwasegun Adeyemi
                  </h4>
                  <p className="text-xs md:text-sm text-gray-500" data-id="element-5042">
                    Alumni, Class of 2000
                  </p>
                </div>
              </div>
              <p className="text-gray-700 italic text-sm md:text-base" data-id="element-5043">
                "My foundation at IGS prepared me for success in medical school
                and beyond as a surgeon."
              </p>
            </div>
            <div className="bg-gray-50 p-5 md:p-6 rounded-lg border border-gray-100 shadow-sm hover:shadow-md transition-shadow" data-id="element-5044">
              <div className="flex items-center mb-4" data-id="element-5045">
                <OptimizedImage src="https://uploadthingy.s3.us-west-1.amazonaws.com/tXB2gESgQyPW5hZWnwiFvU/WhatsApp_Image_2025-08-08_at_06.35.51_%281%29.jpg" alt="Student" className="w-12 h-12 rounded-full mr-4" objectFit="cover" data-id="element-5046" />
                <div data-id="element-5047">
                  <h4 className="font-bold text-blue-900 text-sm md:text-base" data-id="element-5048">
                    Chioma Nwosu
                  </h4>
                  <p className="text-xs md:text-sm text-gray-500" data-id="element-5049">
                    Current Student, SS3
                  </p>
                </div>
              </div>
              <p className="text-gray-700 italic text-sm md:text-base" data-id="element-5050">
                "The opportunities at IGS are incredible. The teachers genuinely
                care about our success."
              </p>
            </div>
          </div>
          <div className="text-center mt-6 md:mt-8" data-id="element-5051">
            <Button href="/testimonials" variant="text" icon={<ArrowRightIcon data-id="element-5053" />} iconPosition="right" data-id="element-5052">
              Read More Testimonials
            </Button>
          </div>
        </div>
      </section>

      {/* School Stats Section */}
      <section className="py-16 md:py-20 bg-blue-900 text-white relative overflow-hidden" data-id="element-5054">
        <div className="absolute inset-0 bg-blue-900 opacity-90 z-0" data-id="element-5055"></div>
        <div className="container mx-auto px-4 relative z-10" data-id="element-5056">
          <div className="text-center mb-10 md:mb-12" data-id="element-5057">
            <h2 className="text-2xl md:text-3xl font-bold mb-3" data-id="element-5058">
              Our Impact in Numbers
            </h2>
            <div className="w-24 h-1 bg-yellow-500 mx-auto" data-id="element-5059"></div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8 text-center mb-12" data-id="element-5060">
            <div className="bg-blue-800/50 p-4 md:p-6 rounded-lg backdrop-blur-sm border border-blue-700/50 transform transition-transform hover:scale-105" data-id="element-5061">
              <div className="inline-block p-3 md:p-4 rounded-full bg-yellow-500/20 mb-3 md:mb-4" data-id="element-5062">
                <CalendarIcon className="h-6 w-6 md:h-8 md:w-8 text-yellow-400" data-id="element-5063" />
              </div>
              <div className="text-3xl md:text-4xl font-bold text-yellow-400 mb-2" data-id="element-5064">
                1956
              </div>
              <p className="text-sm md:text-lg" data-id="element-5065">Established</p>
            </div>
            <div className="bg-blue-800/50 p-4 md:p-6 rounded-lg backdrop-blur-sm border border-blue-700/50 transform transition-transform hover:scale-105" data-id="element-5066">
              <div className="inline-block p-3 md:p-4 rounded-full bg-yellow-500/20 mb-3 md:mb-4" data-id="element-5067">
                <UsersIcon className="h-6 w-6 md:h-8 md:w-8 text-yellow-400" data-id="element-5068" />
              </div>
              <div className="text-3xl md:text-4xl font-bold text-yellow-400 mb-2" data-id="element-5069">
                15,000+
              </div>
              <p className="text-sm md:text-lg" data-id="element-5070">Alumni Worldwide</p>
            </div>
            <div className="bg-blue-800/50 p-4 md:p-6 rounded-lg backdrop-blur-sm border border-blue-700/50 transform transition-transform hover:scale-105" data-id="element-5071">
              <div className="inline-block p-3 md:p-4 rounded-full bg-yellow-500/20 mb-3 md:mb-4" data-id="element-5072">
                <GraduationCapIcon className="h-6 w-6 md:h-8 md:w-8 text-yellow-400" data-id="element-5073" />
              </div>
              <div className="text-3xl md:text-4xl font-bold text-yellow-400 mb-2" data-id="element-5074">
                95%
              </div>
              <p className="text-sm md:text-lg" data-id="element-5075">WAEC Pass Rate</p>
            </div>
            <div className="bg-blue-800/50 p-4 md:p-6 rounded-lg backdrop-blur-sm border border-blue-700/50 transform transition-transform hover:scale-105" data-id="element-5076">
              <div className="inline-block p-3 md:p-4 rounded-full bg-yellow-500/20 mb-3 md:mb-4" data-id="element-5077">
                <TrophyIcon className="h-6 w-6 md:h-8 md:w-8 text-yellow-400" data-id="element-5078" />
              </div>
              <div className="text-3xl md:text-4xl font-bold text-yellow-400 mb-2" data-id="element-5079">
                68+
              </div>
              <p className="text-sm md:text-lg" data-id="element-5080">Years of Excellence</p>
            </div>
          </div>
          {/* Awards Showcase */}
          <div className="max-w-4xl mx-auto" data-id="element-5081">
            <div className="bg-white/10 backdrop-blur-sm p-6 md:p-8 rounded-lg border border-white/20" data-id="element-5082">
              <h3 className="text-xl md:text-2xl font-bold text-center mb-6" data-id="element-5083">
                Recent Achievements
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5084">
                <div className="flex items-center space-x-4" data-id="element-5085">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/izoRqPZv7uCHZWbnJ1rXVJ/WhatsApp_Image_2025-10-24_at_13.20.17_%282%29.jpg" alt="Science competition trophy" className="w-24 h-24 object-contain rounded" data-id="element-5086" />
                  <div data-id="element-5087">
                    <h4 className="font-bold text-yellow-400 mb-1" data-id="element-5088">
                      Science Excellence
                    </h4>
                    <p className="text-sm text-white/90" data-id="element-5089">
                      Winner of Royal Camel Area Science Club Schools Essay
                      Competition
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-4" data-id="element-5090">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/hLZSXSdtdK6mk3VNd7a3xR/WhatsApp_Image_2025-10-24_at_10.01.24.jpg" alt="Academic trophies" className="w-24 h-24 object-contain rounded" data-id="element-5091" />
                  <div data-id="element-5092">
                    <h4 className="font-bold text-yellow-400 mb-1" data-id="element-5093">
                      Academic Honors
                    </h4>
                    <p className="text-sm text-white/90" data-id="element-5094">
                      Multiple awards in inter-school academic competitions
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-gradient-to-r from-yellow-500 to-yellow-400" data-id="element-5095">
        <div className="container mx-auto px-4 text-center" data-id="element-5096">
          <div className="max-w-3xl mx-auto" data-id="element-5097">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-blue-900 mb-4 md:mb-6" data-id="element-5098">
              Ready to Join Our Community?
            </h2>
            <p className="text-base md:text-lg text-blue-900 mb-6 md:mb-8 leading-relaxed px-4" data-id="element-5099">
              Take the first step towards a brighter future at Isonyin Grammar
              School.
            </p>
            <div className="flex flex-wrap justify-center gap-4" data-id="element-5100">
              <Button href="/admissions" variant="secondary" size="lg" icon={<ArrowRightIcon data-id="element-5102" />} iconPosition="right" animation="scale" data-id="element-5101">
                Apply for Admission
              </Button>
              <Button href="/virtual-tour" variant="outline" size="lg" className="border-blue-900 text-blue-900 hover:bg-blue-900/10" data-id="element-5103">
                Take a Virtual Tour
              </Button>
              <Button href="/contact" variant="outline" size="lg" className="border-blue-900 text-blue-900 hover:bg-blue-900/10" data-id="element-5104">
                Contact Us
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>;
};
export default Home;